/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEmpty, isEqual, orderBy } from 'lodash';
import update from 'immutability-helper';
import { StoreActions } from '../../../core/context/store';

import { ApiService } from '../../../core/services';
import {
  StandardSequenceItem,
  ISequenceItem,
  SessionProps,
  IScoutImage,
  InstanceState,
  SimulatorConfigurationProps,
  ParamArrProps,
  ExperimentConfiguration,
  AddSessionPulseSequenceItem,
} from '../models';

const updateSessionDataInternal = (
  actions: StoreActions<any>,
  resetSequences: boolean,
  newData?: SessionProps,
  currentData?: SessionProps
): void => {
  if (!isEmpty(newData)) {
    if (isEmpty(currentData) || newData?.session.id !== currentData?.session.id) {
      actions.setValue(newData);
    } else {
      if (!isEqual(newData?.configuration || {}, currentData?.configuration || {})) {
        actions.updateValue(newData?.configuration, 'configuration');
      }
      if (!isEqual(newData?.course || {}, currentData?.course || {})) {
        actions.updateValue(newData?.course, 'course');
      }
      if (!isEqual(newData?.lab || {}, currentData?.lab || {})) {
        actions.updateValue(newData?.lab, 'lab');
      }
      if (!isEqual(newData?.regions || {}, currentData?.regions || {})) {
        actions.updateValue(newData?.regions, 'regions');
      }
      if (!isEqual(newData?.scoutImages || {}, currentData?.scoutImages || {})) {
        actions.updateValue(newData?.scoutImages, 'scoutImages');
      }
      if (!isEqual(newData?.session || {}, currentData?.session || {})) {
        actions.updateValue(newData?.session, 'session');
      }
      if (resetSequences) {
        actions.updateValue([], 'pulseSequences');
      } else if (!isEqual(newData?.pulseSequences || [], currentData?.pulseSequences || [])) {
        const orderedPsList = orderBy(newData?.pulseSequences || [], (o) => parseInt(o.seqnum as string, 10));
        actions.updateValue(orderedPsList, 'pulseSequences');
      }
    }
  } else {
    actions.setValue(null);
  }
};

export const getSessionPulseSequences =
  (session: number, callbackSuccess?: () => void) =>
  async (actions: StoreActions<ISequenceItem[]>): Promise<void> => {
    try {
      const response = await ApiService.get<ISequenceItem[]>(`v1/sessions/${session}/pulse-sequences`);

      actions.setValue(response.data);
      if (callbackSuccess) callbackSuccess();
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const addToPulseSequence =
  (session: number, sequence: { id: number; name: string }, collectionId: number | null) =>
  async (actions: StoreActions<{ id: number; name: string }>): Promise<void> => {
    try {
      const { id, name } = sequence;
      const formattedParams = {
        pulseSequenceSchemeId: id,
        name,
        collectionId: collectionId || null,
      };

      await ApiService.post(`v1/sessions/${session}/pulse-sequences`, formattedParams);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const addProtocolPulseSequences =
  (
    session: number,
    sequences: { pulseSequences: AddSessionPulseSequenceItem[]; collectionId: number | null },
    callbackSuccess: () => void = () => null
  ) =>
  async (actions: StoreActions<unknown>): Promise<void> => {
    try {
      await ApiService.post(`v1/sessions/${session}/pulse-sequences-many`, sequences);
      callbackSuccess();
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const addLibraryPulseSequence =
  (session: number, sequence: StandardSequenceItem) =>
  async (actions: StoreActions<StandardSequenceItem>): Promise<void> => {
    try {
      await ApiService.post(`v1/sessions/${session}/pulse-sequences/${sequence.id}/from-existing`);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

type GetPulseSequenceProps = {
  callback: (pulseSequence: ParamArrProps) => void;
  pulseSequenceId: number;
  sessionId: number;
};

/**
 * Action to retrieve all data about a pulse sequence for a given pulse sequence id and pass the data to the callback function.
 * This action will not overwrite the store as it is only for temporarily retrieving the data.
 * See getPulseSequenceParams-action below for an action that overwrites the store.
 * @param callback property is the function that will be called upon successful request with the data about the pulse sequence
 */
export const getPulseSequence =
  ({ callback, pulseSequenceId, sessionId }: GetPulseSequenceProps) =>
  async (actions: StoreActions<ParamArrProps | null>): Promise<void> => {
    try {
      const response = await ApiService.get<ParamArrProps>(
        `v1/sessions/${sessionId}/pulse-sequences/${pulseSequenceId}`
      );
      const data = await response.data;

      callback(data);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const getPulseSequenceParams =
  (sessionId: number, sequenceId: number, callback?: (data: any) => void) =>
  async (actions: StoreActions<ParamArrProps | null>): Promise<void> => {
    try {
      actions.setValue(null);
      actions.startLoading();

      const response = await ApiService.get<ParamArrProps>(`v1/sessions/${sessionId}/pulse-sequences/${sequenceId}`);
      actions.setValue(response.data);
      if (callback) {
        callback(response.data);
      }
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const getPulseSequencesParams =
  (sessionId: number, sequenceIds: any[], callback?: (data: any) => void) =>
  async (actions: StoreActions<ParamArrProps | null>): Promise<void> => {
    try {
      const promises: Promise<any>[] = [];

      sequenceIds.forEach((itemId) => {
        promises.push(ApiService.get(`v1/sessions/${sessionId}/pulse-sequences/${itemId}`));
      });

      const responses = await Promise.all(promises);

      // Process the responses here
      if (callback) {
        callback(responses.map((response) => response.data));
      }
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const getLibrarySequenceParams =
  (libraryId: number, skinId: number, callback?: () => void) =>
  async (actions: StoreActions<ParamArrProps | null>): Promise<void> => {
    try {
      actions.setValue(null);
      actions.startLoading();

      const response = await ApiService.get<ParamArrProps>(`/v1/library-parameter-values/${libraryId}/${skinId}`);

      const formatResponse = {
        indicators: response.data.indicators,
        parameters: response.data.parameterValues,
        parameterOptions: response.data.parameterOptions,
        formulas: response.data.formulas,
        spatials: response.data.spatials,
      };

      actions.setValue(formatResponse as ParamArrProps);
      if (callback) {
        callback();
      }
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const getLibraryConfiguration =
  (sessionId: number, libraryId: number) =>
  async (actions: StoreActions<SimulatorConfigurationProps[] | null>): Promise<void> => {
    try {
      actions.setValue(null);
      actions.startLoading();

      const response = await ApiService.get<SimulatorConfigurationProps[]>(
        `v1/sessions/${sessionId}/library-configuration/${libraryId}`
      );

      actions.setValue(response.data);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const getStandardSequenceParams =
  (schemeId: number, skinId: number) =>
  async (actions: StoreActions<ParamArrProps | null>): Promise<void> => {
    try {
      actions.setValue(null);
      actions.startLoading();

      const response = await ApiService.get<ParamArrProps>(`v1/pulse-sequence-parameters/${schemeId}/skins/${skinId}`);

      actions.setValue(response.data);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

// export const getLibraryParameters = (libraryId: number) => async (actions: StoreActions<any>): Promise<void> => {
//   try {
//     actions.setValue(null);
//     actions.startLoading();

//     const response = await ApiService.get<any>(`v1/library-parameter-values/${libraryId}`);

//     actions.setValue(response.data);
//   } catch (err: any) {
//     if (err instanceof ApiError) {
//       actions.setError(err.message);
//     }
//     actions.setError(err.message);
//   }
// };

// export const updateLibraryParameters = (libraryParameters: any) => async (
//   actions: StoreActions<any>
// ): Promise<void> => {
//   try {
//     await ApiService.put<any, any>(`v1/library-parameter-values/`, libraryParameters);
//   } catch (err: any) {
//     if (err instanceof ApiError) {
//       actions.setError(err.message);
//     }
//     actions.setError(err.message);
//   }
// };

// /v1/sessions/{sessionID}/instance-state [get]
// returns the current state of the instance running the specified session
export const getSessionInstanceState =
  (sessionId: number) =>
  async (actions: StoreActions<InstanceState>): Promise<void> => {
    try {
      const response = await ApiService.get<InstanceState>(`v1/sessions/${sessionId}/instance-state`);
      actions.setValue(response.data);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

// returns the current data for the specified session
export const getActiveSession =
  (
    sessionId: number,
    _history: unknown,
    currentData: SessionProps | undefined,
    resetSequences: boolean,
    collectionId?: number
  ) =>
  async (actions: StoreActions<SessionProps>): Promise<void> => {
    try {
      actions.startLoading();

      const response = await ApiService.get<SessionProps>(
        `v1/sessions/${sessionId}?${collectionId !== undefined ? `pulseSequenceCollectionId=${collectionId}` : ''}`
      );

      updateSessionDataInternal(actions, resetSequences, response.data, currentData);
    } catch (err: any) {
      actions.setError(err.message);
      // if (history) {
      //   history.push('/courses');
      // }
    }
  };

export const getScoutImages =
  (sessionId: number, experimentId: number, region?: number, callback?: (scoutImages: IScoutImage[]) => void) =>
  async (actions: StoreActions<IScoutImage[]>): Promise<void> => {
    try {
      actions.startLoading();
      let response;
      if (region && region > 0) {
        response = await ApiService.get<IScoutImage[]>(
          `v1/sessions/${sessionId}/scout-images/${experimentId}?regionId=${region}`
        );
      } else response = await ApiService.get<IScoutImage[]>(`v1/sessions/${sessionId}/scout-images/${experimentId}`);

      actions.setValue(response.data);
      if (callback) {
        callback(response.data);
      }
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const getReconstructedImages =
  (sessionId: number, experimentId: number, callback?: (scoutImages: IScoutImage[]) => void) =>
  async (actions: StoreActions<IScoutImage[]>): Promise<void> => {
    try {
      actions.startLoading();
      const response = await ApiService.get<IScoutImage[]>(
        `v1/sessions/${sessionId}/reconstructed-images/${experimentId}`
      );

      actions.setValue(response.data);
      if (callback) {
        callback(response.data);
      }
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const kSpaceReconstruction =
  (sessionId: number, experimentId: number, imageId: number, data: any) =>
  async (actions: StoreActions<string>): Promise<void> => {
    try {
      actions.startLoading();
      const response = await ApiService.post<any, string>(
        `/v1/sessions/${sessionId}/reconstructed-images/${experimentId}/kspace/${imageId}`,
        data
      );

      actions.setValue(`${response.data}?${new Date().getTime()}`);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const clearSessionData =
  () =>
  async (actions: StoreActions<any>): Promise<void> => {
    try {
      actions.setValue(null);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const updateSequenceList =
  (newList: ISequenceItem[]) =>
  async (actions: StoreActions<unknown>): Promise<void> => {
    try {
      actions.updateValue(newList, 'pulseSequences');
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const updateConfiguration =
  (configuration: ExperimentConfiguration[], name: string, value: string) =>
  async (actions: StoreActions<any>): Promise<void> => {
    try {
      const newConfig = update(configuration, {
        [configuration.findIndex((f) => f.name === name)]: {
          selectedValue: { $set: value },
        },
      });

      actions.updateValue(newConfig, 'configuration');
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const setPulseSequenceNumber =
  (sessionId: number, sequenceId: number, sequenceNumber: string) =>
  async (actions: StoreActions<SessionProps>): Promise<void> => {
    try {
      await ApiService.post(`/v1/sessions/${sessionId}/pulse-sequences/${sequenceId}/move`, {
        position: parseInt(sequenceNumber, 10),
      });
    } catch (err: any) {
      actions.setError(err.message);
    }
  };

export const getDefaultSaturationBands =
  (sequenceId: number) =>
  async (actions: StoreActions<any>): Promise<void> => {
    try {
      const response = await ApiService.get(`/v2/pulse-sequences/${sequenceId}/spatials-default`);
      actions.setValue(response.data);
    } catch (err: any) {
      actions.setError(err.message);
    }
  };
